import {
  BadgeEuro,
  Building2,
  CalendarClock,
  FileCheck,
  Globe,
  Hammer,
  Home,
  Image,
  Landmark,
  MapPin,
  Navigation,
  Plug,
  Settings,
  Sparkles,
  Trees,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { VENTE_SECTIONS, type VenteSection } from "./venteFormFields";

/**
 * MISE EN PAGE de la fiche VENTE — portage du traitement fait sur la fiche
 * location le 2026-08-08 (onglet « Le bien »), qui n'avait alors reçu que le
 * découpage en onglets.
 *
 * Les trois décisions liées, identiques à la location :
 *  1. les blocs sont EMPILÉS EN PLEINE LARGEUR, jamais appariés deux par deux —
 *     une grille aligne ses cellules sur la plus haute, donc un bloc de 4
 *     champs s'étirait à la hauteur de son voisin de 9 ;
 *  2. GRILLE DE 12 COLONNES, chaque champ déclarant sa PORTÉE, au lieu de deux
 *     colonnes de largeur uniforme : « Code postal » attend 4 caractères,
 *     « Adresse » une quarantaine (cf. Baymard : la largeur annonce la
 *     longueur attendue). 12 parce que divisible par 1, 2, 3, 4 et 6 ;
 *  3. les champs qui répondent à UNE MÊME question passent sous un LIBELLÉ
 *     UNIQUE (FieldGroup, motif fieldset/legend de GOV.UK) — « Chauffage —
 *     type » et « Chauffage — énergie » deviennent « Chauffage : type,
 *     énergie ». On SUPPRIME le libellé redondant au lieu de l'abréger.
 *
 * Ce fichier ne décrit QUE la fiche d'édition. Le formulaire de création
 * (BienVenteCreateFormPage) garde sa grille à 2 colonnes et son `span2` :
 * c'est une saisie initiale linéaire, pas une fiche qu'on relit.
 *
 * Garde-fou : `venteFicheLayout.test.ts` vérifie que tout champ de
 * `VENTE_LAYOUT` est placé ici. Un champ oublié n'est pas perdu (la page le
 * rend en fin de section avec une portée par défaut), mais il atterrit au
 * mauvais endroit — le test le signale avant la revue.
 */

// ─── Ordre des blocs, en-tête, champs mis de côté ────────────────────────────

/**
 * Ordre d'affichage des blocs SUR LA FICHE.
 *
 * Il diffère volontairement de `VENTE_SECTIONS`, qui pilote le FORMULAIRE DE
 * CRÉATION : une saisie initiale se déroule dans l'ordre où l'on découvre le
 * bien, une fiche se relit dans l'ordre où l'on cherche l'information. Sur
 * l'onglet « Le bien », cet ordre est celui de la fiche location, bloc pour
 * bloc (Identité, Localisation, Intérieur, Extérieur, Équipements, Bâti) —
 * demande Thibaud 2026-08-09 : les deux fiches doivent se lire pareil.
 */
export const VENTE_FICHE_SECTION_ORDER: VenteSection[] = [
  // Onglet « Le bien » — calque de la fiche location.
  "identification",
  "localisation",
  "interieur",
  "exterieur",
  "equipements",
  "general",
  "copropriete",
  // Onglet « Technique & énergie »
  "raccordements",
  "energie",
  // Onglet « Dossier légal »
  "urbanisme",
  "certificats",
  // Onglet « Prix »
  "finances",
  "disponibilite",
  // Onglet « Commercialisation »
  "proximite",
  "pointsForts",
  "publication",
  "details",
  "medias",
];

/**
 * Champs rendus dans la BANDE D'EN-TÊTE, au-dessus des onglets, et donc retirés
 * de leur bloc. Même principe que la fiche location : l'information qu'on lit
 * en permanence ne doit pas dépendre de l'onglet ouvert.
 */
export const VENTE_FICHE_HEADER_FIELDS = new Set<string>(["disponibilite"]);

/**
 * Champs VOLONTAIREMENT non rendus sur la fiche, en attente d'une place.
 *
 * Sortis de « Identification » le 2026-08-09, le temps que Thibaud parcoure les
 * onglets et leur choisisse une place. Deux ont trouvé la leur depuis :
 * `sousOption` est parti dans Finances le 2026-08-09 puis dans Publication
 * (onglet Commercialisation) le 2026-09-16, et `evaluationInterne` est revenu
 * dans Identification.
 *
 * ⚠️ Ce n'est PAS une suppression. Ils restent dans `VENTE_LAYOUT`, donc le
 * FORMULAIRE DE CRÉATION continue de les proposer, et les colonnes ne bougent
 * pas. Mais tant qu'un champ est ici, on ne peut plus le modifier depuis la
 * fiche : `collaborateurSrs`, ajouté par la PR #57, n'y est plus assignable.
 */
export const VENTE_FICHE_PARKED = new Set<string>([
  "collaborateurSrs",
]);

// ─── Icônes de section ───────────────────────────────────────────────────────

export const VENTE_SECTION_ICON: Record<VenteSection, LucideIcon> = {
  identification: Building2,
  general: Hammer,
  copropriete: Users,
  localisation: MapPin,
  interieur: Home,
  equipements: Settings,
  exterieur: Trees,
  raccordements: Plug,
  energie: Zap,
  urbanisme: Landmark,
  certificats: FileCheck,
  finances: BadgeEuro,
  disponibilite: CalendarClock,
  proximite: Navigation,
  pointsForts: Sparkles,
  publication: Globe,
  details: Globe,
  medias: Image,
};

// ─── Items de la grille ──────────────────────────────────────────────────────

/** Une case d'un groupe. `labelKey` remplace le libellé long du champ. */
export interface VenteGroupUnit {
  key: string;
  labelKey: string;
  /** Poids flex dans la ligne (défaut 1). */
  weight?: number;
  /**
   * Largeur FIXE en pixels, prioritaire sur `weight`. Les poids étant
   * proportionnels à la portée du groupe, une case qui joue le même rôle dans
   * deux groupes de portées différentes n'aurait pas la même taille (leçon de
   * la fiche location : « disponible » mesurait 138 px dans Cave, 96 dans
   * Parking).
   */
  basis?: number;
  /** Petit mot rendu AVANT la case (« sur » entre étage et étages total). */
  beforeKey?: string;
  /** Valeur alignée au centre (compteurs, surfaces, dates courtes). */
  center?: boolean;
}

export type VenteLayoutItem =
  /** Un champ, avec sa portée sur la grille de 12. */
  | { kind: "field"; key: string; span: number; center?: boolean }
  /** Plusieurs champs sous un libellé unique. */
  | { kind: "group"; legendKey: string; span: number; units: VenteGroupUnit[] }
  /**
   * Élément pleine largeur à hauteur variable (répéteur, cases à cocher,
   * galerie). `titleKey` ajoute le filet + intertitre de la fiche location.
   */
  | { kind: "block"; key: string; titleKey?: string };

/** Portée par défaut d'un champ non placé ci-dessous (cf. garde-fou). */
export const VENTE_DEFAULT_SPAN = 4;

// Largeurs FIXES des cases qui jouent le même rôle d'un groupe à l'autre.
const ANNEXE_DISPO_W = 132;
const ANNEXE_NUM_W = 72;

export const VENTE_SECTION_LAYOUT: Record<VenteSection, VenteLayoutItem[]> = {
  // ── Onglet « Le bien » — portées calquées sur la fiche location ────────────
  // Location : referenceInterne 3 · projet 5 · typeBien 4 / referenceExterne 3
  // · codeImmoweb 4. `disponibilite` part dans la bande d'en-tête, et les trois
  // champs mis de côté ne sont plus placés (cf. VENTE_FICHE_PARKED).
  identification: [
    { kind: "field", key: "referenceInterne", span: 3 },
    { kind: "field", key: "projet", span: 5 },
    { kind: "field", key: "typeBien", span: 4 },
    { kind: "field", key: "referenceExterne", span: 3 },
    { kind: "field", key: "codeImmoweb", span: 4 },
    // Note interne (les étoiles) : elle qualifie le bien pour nous, elle se lit
    // donc avec les références — retour Thibaud 2026-08-09.
    { kind: "field", key: "evaluationInterne", span: 5 },
  ],
  // « Bâti » : STRICTEMENT le bloc de la fiche location — mêmes quatre champs,
  // mêmes portées (3 · 3 · 4 · 2), et les deux années en déroulants SÉPARÉS,
  // pas en groupe. Ce qui n'appartient qu'à la vente a son propre bloc
  // ci-dessous (décision Thibaud 2026-08-09).
  general: [
    { kind: "field", key: "anneeConstruction", span: 3, center: true },
    { kind: "field", key: "anneeRenovation", span: 3, center: true },
    { kind: "field", key: "etatBatiment", span: 4 },
    { kind: "field", key: "nbFacades", span: 2, center: true },
  ],
  // « Destination & copropriété » — les quatre champs vente que la location n'a
  // pas. Deux disent l'usage du bâtiment, deux disent la copropriété.
  copropriete: [
    { kind: "field", key: "destinationBatiment", span: 4 },
    { kind: "field", key: "patrimoineProtege", span: 3 },
    { kind: "field", key: "syndic", span: 2 },
    // « Parties en copropriété » : libellé tronqué en dessous de 3 colonnes.
    { kind: "field", key: "partiesCopropriete", span: 3, center: true },
  ],
  // Location : groupe Adresse 10 · region 2 / quartier 4 · typeEnvironnement 4
  // · groupe Étage 4. Identique ici depuis l'ajout du quartier.
  localisation: [
    {
      kind: "group",
      legendKey: "biens.groups.adresseLegend",
      span: 10,
      units: [
        { key: "adresse", labelKey: "biens.groups.adresseRue", weight: 6 },
        { key: "propertyBox", labelKey: "biens.groups.adresseBoite", weight: 1.2, center: true },
        { key: "codePostal", labelKey: "biens.groups.adresseCp", weight: 1.4, center: true },
        { key: "ville", labelKey: "biens.groups.adresseCommune", weight: 3.4, center: true },
      ],
    },
    { kind: "field", key: "region", span: 2 },
    { kind: "field", key: "quartier_id", span: 4 },
    { kind: "field", key: "typeEnvironnement", span: 4 },
    {
      kind: "group",
      legendKey: "biens.groups.etageLegend",
      span: 4,
      units: [
        { key: "etage", labelKey: "biens.groups.etageNiveau", center: true },
        {
          key: "etagesTotal",
          labelKey: "biens.groups.etageTotal",
          beforeKey: "biens.groups.etageSur",
          center: true,
        },
      ],
    },
  ],

  // Location « Intérieur » : groupe Pièces 4 · groupe Surfaces 5 · typeSol 3,
  // puis le répéteur chambres pleine largeur. La vente n'a pas `typeSol` ni
  // `surfaceBrute` ; `typeCuisine` occupe la place du premier, et les champs
  // propres à la vente (chauffage, châssis, vitrage, meublé) suivent sur une
  // deuxième ligne — disposition validée par Thibaud le 2026-08-09.
  interieur: [
    {
      kind: "group",
      legendKey: "biens.groups.piecesLegend",
      span: 4,
      units: [
        { key: "nbChambres", labelKey: "biens.groups.piecesChambres", center: true },
        { key: "nbSdb", labelKey: "biens.groups.piecesSdb", center: true },
        { key: "nbDouches", labelKey: "biens.groups.piecesDouches", center: true },
        { key: "nbToilettes", labelKey: "biens.groups.piecesWc", center: true },
      ],
    },
    {
      kind: "group",
      legendKey: "biens.groups.surfacesLegend",
      span: 5,
      units: [
        { key: "surfaceHabitable", labelKey: "biens.groups.surfacesHabitable", center: true },
        { key: "surfaceSalon", labelKey: "biens.groups.surfacesSalon", center: true },
        { key: "surfaceCuisine", labelKey: "biens.groups.surfacesCuisine", center: true },
      ],
    },
    { kind: "field", key: "typeCuisine", span: 3 },
    {
      kind: "group",
      legendKey: "vente.groups.chauffageLegend",
      span: 4,
      units: [
        { key: "chauffageType", labelKey: "vente.groups.chauffageType" },
        { key: "chauffageEnergie", labelKey: "vente.groups.chauffageEnergie" },
      ],
    },
    { kind: "field", key: "chassisMateriau", span: 3 },
    // doubleVitrage retiré (2026-09-11) : doublon avec le tag Équipements, cf. venteFormFields.ts.
    { kind: "field", key: "meuble", span: 2 },
    // Salle à manger (2026-09-16) : complète la 2e ligne (4 + 3 + 2 + 3 = 12).
    { kind: "field", key: "salleAManger", span: 3 },
    // Répéteur chambres PILOTÉ par `nbChambres` (saisi juste au-dessus) : pas
    // de bouton d'ajout, comme la fiche location.
    { kind: "block", key: "chambresDetail", titleKey: "biens.repeater.chambresTitle" },
  ],
  equipements: [{ kind: "block", key: "equipements" }],
  exterieur: [
    {
      kind: "group",
      legendKey: "biens.groups.caveLegend",
      span: 3,
      units: [
        { key: "caveDisponible", labelKey: "biens.groups.annexeDisponible", basis: ANNEXE_DISPO_W },
        { key: "caveNumero", labelKey: "biens.groups.annexeNumero", basis: ANNEXE_NUM_W },
      ],
    },
    {
      kind: "group",
      legendKey: "biens.groups.parkingLegend",
      span: 3,
      units: [
        { key: "parkingDisponible", labelKey: "biens.groups.annexeDisponible", basis: ANNEXE_DISPO_W },
        { key: "parkingNumero", labelKey: "biens.groups.annexeNumero", basis: ANNEXE_NUM_W },
      ],
    },
    {
      kind: "group",
      legendKey: "biens.groups.garageLegend",
      span: 3,
      units: [
        { key: "garageDisponible", labelKey: "biens.groups.annexeDisponible", basis: ANNEXE_DISPO_W },
        { key: "garageNumero", labelKey: "biens.groups.annexeNumero", basis: ANNEXE_NUM_W },
      ],
    },
    {
      kind: "group",
      legendKey: "biens.groups.jardinCourt",
      span: 3,
      units: [
        { key: "surfaceJardin", labelKey: "biens.repeater.unitSurface", basis: ANNEXE_NUM_W, center: true },
        { key: "orientationJardin", labelKey: "biens.repeater.unitOrientation", center: true },
      ],
    },
    // Terrasses en mode LIBRE (bouton « Ajouter »), comme la fiche location :
    // `nbTerrasses` existe en base depuis la migration 104 mais n'est encore
    // câblé sur aucune des deux fiches.
    { kind: "block", key: "terrasses", titleKey: "biens.repeater.terrassesTitle" },
  ],

  // ── Onglet « Technique & énergie » ─────────────────────────────────────────
  raccordements: [
    { kind: "field", key: "egouts", span: 4 },
    { kind: "field", key: "citerneMazout", span: 4 },
    { kind: "field", key: "borneVehiculeElec", span: 4 },
    {
      kind: "group",
      legendKey: "vente.groups.raccordsLegend",
      span: 6,
      units: [
        { key: "raccordElectricite", labelKey: "vente.groups.utilElec" },
        { key: "raccordGaz", labelKey: "vente.groups.utilGaz" },
        { key: "raccordEau", labelKey: "vente.groups.utilEau" },
      ],
    },
    {
      kind: "group",
      legendKey: "vente.groups.compteursLegend",
      span: 6,
      units: [
        { key: "compteurSepElec", labelKey: "vente.groups.utilElec" },
        { key: "compteurSepGaz", labelKey: "vente.groups.utilGaz" },
        { key: "compteurSepEau", labelKey: "vente.groups.utilEau" },
      ],
    },
  ],
  energie: [
    { kind: "field", key: "classePeb", span: 2, center: true },
    // Les deux dates PEB ont la MÊME largeur (3 colonnes) : elles contiennent
    // la même chose et se lisent côte à côte, une différence de taille ne
    // voulait rien dire. La colonne rendue vient du n° de certificat, qui reste
    // large (4) — il porte une référence d'une trentaine de caractères.
    { kind: "field", key: "certificatPeb", span: 4 },
    { kind: "field", key: "pebDate", span: 3 },
    { kind: "field", key: "pebFinLe", span: 3 },
    {
      kind: "group",
      legendKey: "vente.groups.mesuresLegend",
      span: 5,
      units: [
        { key: "consoKwh", labelKey: "vente.groups.mesureConso", center: true },
        { key: "consoTheorique", labelKey: "vente.groups.mesureConsoTheorique", center: true },
        { key: "emissionCo2", labelKey: "vente.groups.mesureCo2", center: true },
      ],
    },
    {
      kind: "group",
      legendKey: "vente.groups.pebRangeLegend",
      span: 3,
      units: [
        { key: "pebMin", labelKey: "vente.groups.pebMin", center: true },
        { key: "pebMax", labelKey: "vente.groups.pebMax", center: true },
      ],
    },
    { kind: "field", key: "inspectionElecValide", span: 4 },
    { kind: "field", key: "certifElecDate", span: 3 },
    { kind: "field", key: "renovationObligatoire", span: 5 },
    // Pas de `titleKey` : la dropzone porte déjà son propre libellé, un
    // intertitre l'afficherait deux fois.
    { kind: "block", key: "pebCertificatPdf" },
  ],

  // ── Onglet « Dossier légal » ───────────────────────────────────────────────
  urbanisme: [
    { kind: "field", key: "permisConstruire", span: 4 },
    { kind: "field", key: "planCadastral", span: 4 },
    { kind: "field", key: "permisLotissement", span: 4 },
    { kind: "field", key: "droitPreemption", span: 4 },
    { kind: "field", key: "droitPreferenceLocataire", span: 4 },
    { kind: "field", key: "infractionUrbanistique", span: 4 },
    { kind: "field", key: "destinationUrbanistique", span: 5 },
    { kind: "field", key: "zoneInondable", span: 4 },
    { kind: "field", key: "certifInondation", span: 3 },
    {
      kind: "group",
      legendKey: "vente.groups.scoresLegend",
      span: 3,
      units: [
        { key: "pScore", labelKey: "vente.groups.scoreP", center: true },
        { key: "gScore", labelKey: "vente.groups.scoreG", center: true },
      ],
    },
    { kind: "field", key: "registreFlamandConsulte", span: 4 },
    { kind: "field", key: "mesureJudiciaire", span: 5 },
  ],
  certificats: [
    {
      kind: "group",
      legendKey: "vente.groups.amianteLegend",
      span: 8,
      units: [
        { key: "certificatAmiante", labelKey: "vente.groups.amianteCertificat" },
        { key: "certificatAmianteDate", labelKey: "vente.groups.amianteDate" },
        { key: "construitAvant2001", labelKey: "vente.groups.amianteAvant2001" },
      ],
    },
    { kind: "field", key: "attestationSol", span: 4 },
  ],

  // ── Onglet « Prix » ────────────────────────────────────────────────────────
  finances: [
    {
      kind: "group",
      legendKey: "vente.groups.prixLegend",
      span: 6,
      units: [
        { key: "prix", labelKey: "vente.groups.prixVente", weight: 2, center: true },
        { key: "prixM2", labelKey: "vente.groups.prixM2", weight: 1.6, center: true },
        { key: "masquerPrix", labelKey: "vente.groups.prixMasque", basis: 96 },
      ],
    },
    { kind: "field", key: "immeubleRapport", span: 3 },
    { kind: "field", key: "sousRegimeTva", span: 3 },
    {
      kind: "group",
      legendKey: "vente.groups.cadastreLegend",
      span: 6,
      units: [
        { key: "revenuCadastral", labelKey: "vente.groups.cadastreRc", center: true },
        { key: "rcIndexe", labelKey: "vente.groups.cadastreRcIndexe", center: true },
        { key: "precompteImmobilier", labelKey: "vente.groups.cadastrePrecompte", center: true },
      ],
    },
    {
      kind: "group",
      legendKey: "vente.groups.loyersLegend",
      span: 6,
      units: [
        { key: "loyerHorsCharges", labelKey: "vente.groups.loyerHorsCharges", center: true },
        { key: "dernierLoyer", labelKey: "vente.groups.loyerDernier", center: true },
        { key: "dernierLoyerDate", labelKey: "vente.groups.loyerDate", weight: 1.4 },
      ],
    },
    // « Sous option (Négo en cours) » vivait ici (décision 2026-08-09) ;
    // déplacée dans Publication le 2026-09-16 (demande Thibaud).
  ],
  disponibilite: [
    { kind: "field", key: "seraDispoPour", span: 5 },
    { kind: "field", key: "venteDisponibiliteDate", span: 3 },
  ],

  // ── Onglet « Commercialisation » ───────────────────────────────────────────
  proximite: [{ kind: "block", key: "proximite" }],
  pointsForts: [{ kind: "field", key: "pointsForts", span: 12 }],
  publication: [
    { kind: "field", key: "statutPublication", span: 4 },
    { kind: "field", key: "visibiliteImmoweb", span: 4 },
    { kind: "field", key: "alerteTexte", span: 4 },
    { kind: "field", key: "photosOk", span: 4 },
    { kind: "field", key: "videoOk", span: 4 },
    { kind: "field", key: "textesOk", span: 4 },
    // Sous option ici (et plus dans Finances) — demande Thibaud 2026-09-16.
    { kind: "field", key: "sousOption", span: 12 },
  ],
  // Les 3 cartes FR/NL/EN ont leur propre rendu (renderDetailsSection).
  details: [],
  medias: [
    { kind: "field", key: "videoYoutubeUrl", span: 8 },
    { kind: "block", key: "photos" },
  ],
};

/** Toutes les clés de champ placées par le layout, section par section. */
export function venteLayoutKeys(section: VenteSection): string[] {
  return VENTE_SECTION_LAYOUT[section].flatMap((item) =>
    item.kind === "group" ? item.units.map((u) => u.key) : [item.key],
  );
}

/** Toutes les clés placées, toutes sections confondues. */
export function allVenteLayoutKeys(): Set<string> {
  return new Set(VENTE_SECTIONS.flatMap((s) => venteLayoutKeys(s)));
}
